import React from 'react'
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTheme } from 'react-admin';

type InputProps = {
    src: string;
    open: boolean;
    type?: string;
    onClose: () => void;
    images?: string[];      // full array for gallery navigation
    initialIndex?: number;  // which image to start on
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;

const ImagePreviewerDialog = ({ src, open, onClose, type, images, initialIndex = 0 }: InputProps) => {
    const [rotation, setRotation] = React.useState(0);
    const [scale, setScale] = React.useState(1);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [currentIdx, setCurrentIdx] = React.useState(initialIndex);
    const dragStart = React.useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
    const [theme] = useTheme();
    const currentTheme = theme.palette?.mode;

    const canNav = images && images.length > 1;
    const effectiveSrc = images && images.length > 0 ? images[currentIdx] : src;
    const effectiveType = effectiveSrc && /\.(mp4|webm|ogg)$/i.test(effectiveSrc) ? 'video' : (type ?? 'image');

    // Re-sync index whenever the dialog opens with a new initialIndex
    React.useEffect(() => {
        if (open) setCurrentIdx(initialIndex);
    }, [open, initialIndex]);

    const reset = React.useCallback(() => {
        setRotation(0);
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, []);

    // Reset zoom/rotation whenever the displayed image changes
    React.useEffect(() => {
        reset();
    }, [currentIdx, reset]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const zoomIn = () => setScale(s => Math.min(s + ZOOM_STEP, MAX_SCALE));
    const zoomOut = () => setScale(s => Math.max(s - ZOOM_STEP, MIN_SCALE));
    const rotateLeft = () => setRotation(r => r - 90);
    const rotateRight = () => setRotation(r => r + 90);

    const goPrev = React.useCallback(() => {
        if (!images?.length) return;
        setCurrentIdx(i => (i - 1 + images.length) % images.length);
    }, [images]);

    const goNext = React.useCallback(() => {
        if (!images?.length) return;
        setCurrentIdx(i => (i + 1) % images.length);
    }, [images]);

    // Keyboard ← / → navigation
    React.useEffect(() => {
        if (!open || !canNav) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, canNav, goPrev, goNext]);

    // Wheel zoom
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        setScale(s => Math.min(Math.max(s + delta, MIN_SCALE), MAX_SCALE));
    };

    // Drag to pan
    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY, posX: position.x, posY: position.y };
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPosition({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        dragStart.current = null;
    };

    // Reset pan when zoom returns to 1
    React.useEffect(() => {
        if (scale === 1) setPosition({ x: 0, y: 0 });
    }, [scale]);

    const isDark = currentTheme === 'dark';
    const toolbarBg = isDark ? 'rgb(45, 54, 67)' : '#f5f5f5';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { maxHeight: '95vh', height: '95vh', m: 1, overflow: 'hidden' } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Header */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2}
                    py={1}
                    sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
                >
                    <Typography variant="subtitle1" fontWeight={500}>
                        {effectiveType === 'video' ? 'Video Preview' : 'Image Preview'}
                        {canNav && (
                            <Typography component="span" variant="caption" sx={{ ml: 1.5, color: 'text.secondary' }}>
                                {currentIdx + 1} / {images!.length}
                            </Typography>
                        )}
                    </Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Viewer area */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: isDark ? '#1a1f2a' : '#e9e9e9',
                        cursor: effectiveType !== 'video' ? (scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default') : 'default',
                        userSelect: 'none',
                        minHeight: 560,
                    }}
                    onWheel={effectiveType !== 'video' ? handleWheel : undefined}
                    onMouseDown={effectiveType !== 'video' ? handleMouseDown : undefined}
                    onMouseMove={effectiveType !== 'video' ? handleMouseMove : undefined}
                    onMouseUp={effectiveType !== 'video' ? handleMouseUp : undefined}
                    onMouseLeave={effectiveType !== 'video' ? handleMouseUp : undefined}
                >
                    {/* Prev arrow */}
                    {canNav && (
                        <IconButton
                            onClick={goPrev}
                            sx={{
                                position: 'absolute', left: 8, top: '50%',
                                transform: 'translateY(-50%)', zIndex: 10,
                                bgcolor: 'rgba(0,0,0,0.45)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                    )}

                    {/* Next arrow */}
                    {canNav && (
                        <IconButton
                            onClick={goNext}
                            sx={{
                                position: 'absolute', right: 8, top: '50%',
                                transform: 'translateY(-50%)', zIndex: 10,
                                bgcolor: 'rgba(0,0,0,0.45)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    )}

                    {effectiveType === 'video' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
                            <video
                                controls
                                loop
                                key={effectiveSrc}
                                style={{ maxWidth: '100%', maxHeight: 'calc(95vh - 110px)', display: 'block' }}
                            >
                                <source src={effectiveSrc} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <img
                                src={effectiveSrc}
                                alt="preview"
                                draggable={false}
                                style={{
                                    transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${scale})`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease',
                                    transformOrigin: 'center center',
                                    maxWidth: '100%',
                                    maxHeight: 'calc(95vh - 110px)',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {/* Toolbar */}
                {effectiveType !== 'video' && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            px: 2,
                            py: 0.75,
                            background: toolbarBg,
                            borderTop: 1,
                            borderColor: 'divider',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title="Rotate left">
                            <IconButton size="small" onClick={rotateLeft}>
                                <RotateLeftIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate right">
                            <IconButton size="small" onClick={rotateRight}>
                                <RotateRightIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{ width: 1, bgcolor: 'divider', height: 24, mx: 0.5 }} />

                        <Tooltip title="Zoom out">
                            <span>
                                <IconButton size="small" onClick={zoomOut} disabled={scale <= MIN_SCALE}>
                                    <ZoomOutIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(scale * 100)}%
                        </Typography>

                        <Tooltip title="Zoom in">
                            <span>
                                <IconButton size="small" onClick={zoomIn} disabled={scale >= MAX_SCALE}>
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Box sx={{ width: 1, bgcolor: 'divider', height: 24, mx: 0.5 }} />

                        <Tooltip title="Reset view">
                            <IconButton size="small" onClick={reset}>
                                <ZoomOutMapIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default ImagePreviewerDialog;
